// src/components/shared/PostCard.js
import React, { useState, useRef, useEffect } from "react";
import { Heart, MessageCircle, Share2, Lock } from "lucide-react";
import Card from "../ui/Card";
import ShareModal from "./ShareModal";
import CommentSection from "./CommentSection";

import FanLoyaltyBadge from "../../portals/fan/components/FanLoyaltyBadge";

const PostCard = ({ post, author, isCommentsVisible, onToggleComments }) => {
  const creator = post.creator || author;
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(
    post.stats?.likes ?? post.likes ?? 0
  );
  const [isShareModalOpen, setShareModalOpen] = useState(false);
  const cardRef = useRef(null);

  // ✨ Auto-inject image if premium but missing (for the "Vault" effect demo)
  const postImage = post.imageUrl || (post.isPremium ? `https://placehold.co/600x600/1e293b/FFFFFF?text=EXCLUSIVE+PREVIEW` : null);

  useEffect(() => {
    if (isCommentsVisible) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 100);
    }
  }, [isCommentsVisible]);

  if (!creator) return null;

  const content = post.content || post.title || "";
  const initialCommentsCount = post.stats?.comments ?? post.comments ?? 0;

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikeCount(isLiked ? likeCount - 1 : likeCount + 1);
  };

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const postUrl = `http://localhost:3000/fan/post/${post.id}`;

  return (
    <>
      <Card
        ref={cardRef}
        className="!p-0 shadow-lg shadow-slate-200/40 rounded-[2.5rem] border border-slate-100 overflow-hidden bg-white group transition-all duration-300"
      >
        <div className="p-4 flex items-center">
          <div className="relative p-0.5 rounded-full bg-gradient-to-tr from-primary to-indigo-500 mr-4">
             <img
                src={creator.profilePicture || creator.profilePictureUrl}
                alt={creator.name || creator.fullName}
                className="w-12 h-12 rounded-full object-cover border-2 border-white"
             />
          </div>
          <div className="flex-grow">
            <div className="flex items-center gap-2">
              <p className="font-bold text-sm text-slate-900 tracking-tight">
                {creator.name || creator.fullName}
              </p>
              {post.isPremium ? (
                  <FanLoyaltyBadge tier="top" size="xs" />
              ) : (
                  <FanLoyaltyBadge tier="fan" size="xs" />
              )}
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              {post.timestamp || post.publishDate}
            </p>
          </div>
        </div>

        <div className="px-5 pb-4">
          <p className="text-slate-800 text-[13px] font-medium leading-relaxed whitespace-pre-wrap">
            {content}
          </p>
        </div>

        {postImage && (
          <div className="relative w-full aspect-square overflow-hidden border-y border-slate-100/50">
            <img
              src={postImage}
              alt="Post content"
              className={`w-full h-full object-cover transition-all duration-500 ${post.isPremium ? 'blur-2xl scale-110 opacity-60' : 'group-hover:scale-105'}`}
            />
            {post.isPremium && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/10 backdrop-blur-md">
                    <div className="p-4 bg-white/90 rounded-3xl shadow-2xl flex flex-col items-center text-center max-w-[80%]">
                        <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-3">
                            <Lock size={24} />
                        </div>
                        <h4 className="text-sm font-black text-slate-900 uppercase tracking-tighter">Exclusive Content</h4>
                        <p className="text-[11px] text-slate-500 font-bold mt-1">Unlock this post and 42 others by joining the Fan Club.</p>
                        <button className="mt-4 px-8 py-3 premium-gradient text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-primary/30 transition-all active:scale-95 hover:neon-glow border border-white/20">
                            Join to Unlock
                        </button>
                    </div>
                </div>
            )}
          </div>
        )}

        {!post.isPremium && (
          <div className="px-4 py-3 flex justify-around items-center text-slate-500 border-t border-slate-100/50">
            <button
              onClick={handleLike}
              className={`flex flex-col items-center gap-0.5 py-1 px-4 rounded-xl transition-all ${
                isLiked
                  ? "text-red-500"
                  : "hover:text-red-500"
              }`}
            >
              <Heart size={20} fill={isLiked ? "currentColor" : "none"} strokeWidth={2.5} />
              <span className="font-black text-[10px]">{likeCount}</span>
            </button>
            <button
              onClick={onToggleComments}
              className="flex flex-col items-center gap-0.5 py-1 px-4 rounded-xl hover:text-primary transition-all"
            >
              <MessageCircle size={20} strokeWidth={2.5} />
              <span className="font-black text-[10px]">
                {initialCommentsCount}
              </span>
            </button>
            <button
              onClick={handleShare}
              className="flex flex-col items-center gap-0.5 py-1 px-4 rounded-xl hover:text-slate-900 transition-all font-black text-[10px]"
            >
              <Share2 size={20} strokeWidth={2.5} />
              SHARE
            </button>
          </div>
        )}

        {isCommentsVisible && (
          <div className="border-t border-slate-100 bg-slate-50/50">
            <CommentSection initialComments={post.comments} />
          </div>
        )}
      </Card>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setShareModalOpen(false)}
        postUrl={postUrl}
      />
    </>
  );
};

export default PostCard;
